import React, { createContext, useContext, useState, useEffect } from 'react';
import { useStytch, useStytchUser, useStytchSession } from '@stytch/react';
import { eventBus } from '@/modules/core/events';
import { events } from '../events';
import * as Sentry from '@sentry/browser';
import { 
  trackSignIn, 
  trackSignOut, 
  trackUserIdentification, 
  resetUserIdentification,
  trackTikTokSubscribe 
} from '@/services/analytics';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const stytch = useStytch();
  const { user } = useStytchUser();
  const { session } = useStytchSession();

  const [loading, setLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState(null);
  const [hasRecordedLogin, setHasRecordedLogin] = useState(false);
  const [lastTrackedSessionId, setLastTrackedSessionId] = useState(null);

    // Handle OAuth redirect - process tokens and redirect when authenticated
  useEffect(() => {
    if (!stytch) return;

    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const tokenType = urlParams.get('stytch_token_type');
    
    // Process magic link tokens if present
    if (token && tokenType === 'magic_links') {
      console.log('Processing magic link token...');
      stytch.magicLinks.authenticate(token, {
        session_duration_minutes: 60,
      });
    }
    
    // Don't auto-redirect - let the authenticate page handle showing success message
  }, [stytch]);

  // Convert Stytch user to your app's format (email-as-identifier)
  const createAppUser = (stytchUser) => {
    if (!stytchUser) return null;

    return {
      id: stytchUser.user_id,
      email: stytchUser.emails?.[0]?.email || null,
      fullName: stytchUser.name?.first_name ? `${stytchUser.name.first_name} ${stytchUser.name.last_name || ''}`.trim() : null,
      created_at: stytchUser.created_at,
      // Auth provider info for reference only
      authProvider: {
        provider: 'stytch'
      }
    };
  };

  const shouldTrackSignIn = (newSession) => {
    // Only track if we have a new session and haven't tracked this session yet
    return newSession?.session_token && lastTrackedSessionId !== newSession.session_token;
  };

  // Determine authentication method from session
  const getAuthMethod = (session) => {
    if (!session) return 'email';
    
    // Check authentication factors to determine method
    const factors = session.authentication_factors || [];
    
    for (const factor of factors) {
      if (factor.type === 'oauth') {
        // Extract OAuth provider from factor
        if (factor.oauth_provider_type) {
          return `oauth_${factor.oauth_provider_type.toLowerCase()}`;
        }
        return 'oauth';
      }
      if (factor.type === 'otp') {
        return 'email_otp';
      }
      if (factor.type === 'magic_link') {
        return 'email_magic_link';
      }
    }
    
    return 'email';
  };

  // Handle auth state changes
  useEffect(() => {
    let token = null;
    if (session && stytch) {
      try {
        const tokens = stytch.session.getTokens();
        token = tokens?.session_token || null;

        // Track user identification if this is a new session
        if (user?.emails?.[0]?.email && shouldTrackSignIn(session)) {
          const userEmail = user.emails[0].email;
          const authMethod = getAuthMethod(session);
          
          trackUserIdentification(userEmail, {
            email: userEmail,
            session_type: 'new',
            auth_provider: 'stytch',
            auth_method: authMethod
          });

          // Track sign-in event with specific method
          trackSignIn(authMethod);

          // Track TikTok subscription for new sign-ups
          trackTikTokSubscribe('waitlist');

          // Emit events with email-based user object for consistency
          eventBus.publish(events.USER_SIGNED_IN, { 
            user: createAppUser(user),
            authMethod: authMethod
          });

          setLastTrackedSessionId(session.session_token);
          setHasRecordedLogin(false);
        } else if (user?.emails?.[0]?.email && !shouldTrackSignIn(session)) {
          // Existing session - track user identification only
          const authMethod = getAuthMethod(session);
          trackUserIdentification(user.emails[0].email, {
            email: user.emails[0].email,
            session_type: 'existing',
            auth_provider: 'stytch',
            auth_method: authMethod
          });
        }
      } catch (error) {
        console.error('Error getting session tokens:', error);
        Sentry.captureException(error, {
          extra: { action: 'getSessionTokens' }
        });
        token = null;
      }
    }

    setSessionToken(token);
    setLoading(false);
  }, [session, user, stytch, lastTrackedSessionId]);

  // Handle user database operations
  useEffect(() => {
    if (user?.emails?.[0]?.email && sessionToken && !hasRecordedLogin) {
      const recordLoginAndSaveUser = async () => {
        try {
          // Mark as recording immediately to prevent double execution
          setHasRecordedLogin(true);
          await saveUserToDatabase();
          console.log('Auth Provider: User successfully processed');
        } catch (error) {
          console.error('Auth Provider: Error in post-login operations:', error);
          // Reset flag if there was an error so it can retry
          setHasRecordedLogin(false);
        }
      };
      
      recordLoginAndSaveUser();
    }
  }, [user, sessionToken, hasRecordedLogin]);

  const signOut = async () => {
    try {
      // Track sign-out event
      trackSignOut();
      
      // Reset user identification
      resetUserIdentification();
      
      // Clear tracking state
      setLastTrackedSessionId(null);
      setHasRecordedLogin(false);

      await stytch.session.revoke();
      
      eventBus.publish(events.USER_SIGNED_OUT, {});
    } catch (error) {
      console.error('Error signing out:', error);
      Sentry.captureException(error, {
        extra: { action: 'signOut' }
      });
    }
  };

  // Helper function for authenticated API calls
  const authFetch = async (url, options = {}) => {
    return fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${sessionToken}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
  };

  const saveUserToDatabase = async () => {
    try {
      console.log('Auth Provider: Saving user to database');
      const response = await authFetch('/api/users', {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Auth Provider: User saved to database successfully');
      return data;
    } catch (error) {
      console.error('Auth Provider: Error saving user to database:', error);
      Sentry.captureException(error, {
        extra: { 
          action: 'saveUserToDatabase',
          userId: user?.user_id 
        }
      });
      throw error;
    }
  };

  const value = {
    user: createAppUser(user),
    sessionToken,
    loading,
    signOut,
    authFetch
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};